import {sortBy} from 'lodash';

import {isTextAnnotation, resolveAnchor} from './annotationAnchors';
import {scrollerContentOffset} from './annotationIndicatorPlacement';
import type {TextProjection} from './annotationTextProjection';
import {normalizedRangeToDomRange} from './annotationTextProjection';
import type {Annotation, TextAnnotation} from './annotationTypes';

/** The `xs` indicator button's height — closer centers cannot render apart. */
const INDICATOR_SLOT_PX = 24;

export interface AnnotationNoteLine {
  /** The line's vertical center within the scroller's scrolled content. */
  center: number;
  /** The noted marks on this line, in the order they arrived. */
  annotations: Annotation[];
}

export interface MeasuredNote {
  annotation: TextAnnotation;
  center: number;
}

/**
 * Where each noted mark's first line box sits in the scroller's scrolled content,
 * in document order. A mark whose anchor no longer resolves is dropped, as the
 * paint drops it; whole-supplement marks surface on the supplement instead.
 */
export function measureNotes(
  annotations: Annotation[],
  projection: TextProjection,
  scroller: HTMLElement
): MeasuredNote[] {
  const contentOffset = scrollerContentOffset(scroller);
  return sortBy(annotations.filter(isTextAnnotation), ({anchor}) => anchor.start).flatMap(
    (annotation) => {
      const resolved = resolveAnchor(annotation.anchor, projection);
      const range = resolved && normalizedRangeToDomRange(projection, resolved);
      const firstLine = range?.getClientRects()[0];
      if (!firstLine) {
        return [];
      }
      return [{annotation, center: firstLine.top + firstLine.height / 2 + contentOffset}];
    }
  );
}

/**
 * One entry per indicator that fits, each keeping input order and its first
 * note's center. Regions don't matter: a table row's cells share a slot.
 */
export function groupNotesByLine(notes: MeasuredNote[]): AnnotationNoteLine[] {
  const lines: AnnotationNoteLine[] = [];
  notes.forEach(({annotation, center}) => {
    const line = lines.find((candidate) => Math.abs(candidate.center - center) < INDICATOR_SLOT_PX);
    if (line) {
      line.annotations.push(annotation);
      return;
    }
    lines.push({center, annotations: [annotation]});
  });
  return lines;
}
